import { useRef, useState } from "react";
import { Sparkles, Cloud, Check, Eye, EyeOff, Loader2, CheckCircle2, AlertTriangle, ShieldAlert, Database, Download, Upload } from "lucide-react";
import { STYLES, useStyleStore } from "@/store/styleStore";
import { useAIStore } from "@/store/aiStore";
import { getProvider, AI_MODELS, AIError } from "@/core/ai";
import { downloadSnapshot, importSnapshotFile } from "@/core/data/backup";

export default function Settings() {
  const { styleId, setStyle } = useStyleStore();

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      {/* Visual style gallery — fully working */}
      <section className="rounded-2xl border border-border bg-surface p-6">
        <h2 className="font-display text-lg font-semibold text-text">Visual style</h2>
        <p className="mt-1 text-sm text-muted">
          Pick a look (applies instantly). Tell me which code name you want to keep
          and I'll make it the default. You can also switch from the palette button
          in the top bar.
        </p>

        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          {STYLES.map((s) => {
            const active = s.id === styleId;
            return (
              <button
                key={s.id}
                onClick={() => setStyle(s.id)}
                className={[
                  "group overflow-hidden rounded-xl border text-left transition-all",
                  active ? "border-primary ring-1 ring-primary" : "border-border hover:border-primary/50",
                ].join(" ")}
              >
                {/* Mini live preview painted with the style's own tokens */}
                <div
                  className="flex h-20 items-center gap-2 px-4"
                  style={{ backgroundColor: `rgb(${s.tokens.bg})` }}
                >
                  <span
                    className="rounded-lg px-2.5 py-1 text-xs font-bold"
                    style={{
                      backgroundColor: `rgb(${s.tokens.primary})`,
                      color: `rgb(${s.tokens.primaryFg})`,
                      fontFamily: s.fontDisplay,
                    }}
                  >
                    H
                  </span>
                  <span className="flex flex-col">
                    <span style={{ color: `rgb(${s.tokens.text})`, fontFamily: s.fontDisplay }} className="text-sm font-semibold">
                      Aa Game Design
                    </span>
                    <span className="flex gap-1.5 pt-1">
                      <span className="h-2 w-8 rounded-full" style={{ backgroundColor: `rgb(${s.tokens.accent})` }} />
                      <span className="h-2 w-5 rounded-full" style={{ backgroundColor: `rgb(${s.tokens.surface2})` }} />
                      <span className="h-2 w-3 rounded-full" style={{ backgroundColor: `rgb(${s.tokens.muted})` }} />
                    </span>
                  </span>
                </div>
                <div className="flex items-center justify-between gap-2 border-t border-border bg-surface px-4 py-2.5">
                  <span className="min-w-0">
                    <span className="flex items-center gap-2">
                      <span className="font-display text-sm font-semibold tracking-wide text-text">
                        {s.name}
                      </span>
                      {active && <Check className="h-3.5 w-3.5 text-primary" />}
                    </span>
                    <span className="block truncate text-xs text-muted">{s.tagline}</span>
                  </span>
                  <span className="shrink-0 rounded-full bg-surface-2 px-2 py-0.5 text-[10px] uppercase text-muted">
                    {s.scheme}
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      </section>

      <AISettings />

      <DataSettings />

      <section className="rounded-2xl border border-border bg-surface p-6 opacity-80">
        <div className="flex items-center gap-2">
          <Cloud className="h-5 w-5 text-accent" />
          <h2 className="font-display text-lg font-semibold text-text">Cloud Sync</h2>
          <span className="ml-auto rounded-full bg-accent/15 px-2.5 py-0.5 text-xs font-medium text-accent">
            Later
          </span>
        </div>
        <p className="mt-2 text-sm text-muted">
          Automatic sync across devices behind a login is planned. For now, use Export / Import
          above to move your data between computers.
        </p>
      </section>
    </div>
  );
}

function DataSettings() {
  const [busy, setBusy] = useState(false);
  const [includeKey, setIncludeKey] = useState(false);
  const [msg, setMsg] = useState<{ kind: "ok" | "error"; text: string } | null>(null);
  const [pending, setPending] = useState<File | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  async function doExport() {
    setBusy(true);
    setMsg(null);
    try {
      await downloadSnapshot(includeKey);
      setMsg({ kind: "ok", text: "Backup downloaded." });
    } catch {
      setMsg({ kind: "error", text: "Couldn't create the backup." });
    } finally {
      setBusy(false);
    }
  }

  async function doImport() {
    if (!pending) return;
    setBusy(true);
    setMsg(null);
    try {
      await importSnapshotFile(pending);
      // Reload so every store re-hydrates from the imported data.
      window.location.reload();
    } catch (e) {
      setMsg({ kind: "error", text: e instanceof Error ? e.message : "Import failed." });
      setBusy(false);
      setPending(null);
    }
  }

  return (
    <section className="rounded-2xl border border-border bg-surface p-6">
      <div className="flex items-center gap-2">
        <Database className="h-5 w-5 text-primary" />
        <h2 className="font-display text-lg font-semibold text-text">Your data</h2>
      </div>
      <p className="mt-2 text-sm text-muted">
        Move everything — Library, Content Studio items, bookmarks, notes, history, and XP — between
        computers. Export a backup file here, then import it on the other machine.
      </p>

      {/* Export */}
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <button
          onClick={doExport}
          disabled={busy}
          className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-fg shadow-glow transition-transform enabled:hover:scale-[1.02] disabled:opacity-50"
        >
          <Download className="h-4 w-4" /> Export backup
        </button>
        <label className="inline-flex cursor-pointer items-center gap-2 text-sm text-muted">
          <input type="checkbox" checked={includeKey} onChange={(e) => setIncludeKey(e.target.checked)} className="accent-[rgb(var(--primary))]" />
          Include AI API key
        </label>
      </div>

      {/* Import */}
      <div className="mt-4 border-t border-border pt-4">
        <input
          ref={fileInput}
          type="file"
          accept="application/json,.json"
          hidden
          onChange={(e) => { setPending(e.target.files?.[0] ?? null); setMsg(null); }}
        />
        {!pending ? (
          <button
            onClick={() => fileInput.current?.click()}
            className="inline-flex items-center gap-2 rounded-lg border border-border bg-surface-2 px-4 py-2 text-sm font-medium text-text transition-colors hover:bg-border/40"
          >
            <Upload className="h-4 w-4" /> Import backup…
          </button>
        ) : (
          <div className="flex flex-wrap items-center gap-3 rounded-lg border border-amber-400/40 bg-amber-400/5 px-3 py-2.5">
            <AlertTriangle className="h-4 w-4 shrink-0 text-amber-400" />
            <span className="text-sm text-text">Import <span className="font-medium">{pending.name}</span>? This replaces all current data on this device.</span>
            <div className="ml-auto flex gap-2">
              <button onClick={() => setPending(null)} className="rounded-md border border-border px-3 py-1 text-xs text-muted hover:text-text">Cancel</button>
              <button onClick={doImport} disabled={busy} className="inline-flex items-center gap-1.5 rounded-md bg-amber-400 px-3 py-1 text-xs font-semibold text-black disabled:opacity-50">
                {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : null} Replace &amp; import
              </button>
            </div>
          </div>
        )}
      </div>

      {msg && (
        <p className={`mt-3 flex items-center gap-1.5 text-sm ${msg.kind === "ok" ? "text-emerald-400" : "text-amber-400"}`}>
          {msg.kind === "ok" ? <CheckCircle2 className="h-4 w-4" /> : <AlertTriangle className="h-4 w-4" />} {msg.text}
        </p>
      )}
    </section>
  );
}

type TestState = { kind: "idle" | "testing" | "ok" | "error"; message?: string };

function AISettings() {
  const { enabled, apiKey, model, setEnabled, setApiKey, setModel } = useAIStore();
  const [showKey, setShowKey] = useState(false);
  const [test, setTest] = useState<TestState>({ kind: "idle" });
  const abort = useRef<AbortController | null>(null);

  async function runTest() {
    if (!apiKey.trim()) {
      setTest({ kind: "error", message: "Enter an API key first." });
      return;
    }
    abort.current?.abort();
    const controller = new AbortController();
    abort.current = controller;
    setTest({ kind: "testing" });
    try {
      await getProvider({ apiKey: apiKey.trim(), model }).test(controller.signal);
      if (!controller.signal.aborted) setTest({ kind: "ok", message: "Connected — key and model work." });
    } catch (e) {
      if (controller.signal.aborted) return;
      setTest({ kind: "error", message: e instanceof AIError ? e.message : "Couldn't verify the connection." });
    }
  }

  return (
    <section className="rounded-2xl border border-border bg-surface p-6">
      <div className="flex items-center gap-2">
        <Sparkles className="h-5 w-5 text-primary" />
        <h2 className="font-display text-lg font-semibold text-text">AI Feedback</h2>
        <label className="ml-auto inline-flex cursor-pointer items-center gap-2">
          <span className="text-xs font-medium text-muted">{enabled ? "On" : "Off"}</span>
          <span className="relative inline-block h-6 w-11">
            <input
              type="checkbox"
              className="peer sr-only"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
            />
            <span className="absolute inset-0 rounded-full bg-surface-2 transition-colors peer-checked:bg-primary" />
            <span className="absolute left-0.5 top-0.5 h-5 w-5 rounded-full bg-white transition-transform peer-checked:translate-x-5" />
          </span>
        </label>
      </div>
      <p className="mt-2 text-sm text-muted">
        Use your own Anthropic (Claude) API key to get mentor-style feedback on your challenge and
        interview answers. Calls go straight from this app to Anthropic — nothing passes through any
        Hippo server.
      </p>

      <div className={enabled ? "mt-5 space-y-4" : "pointer-events-none mt-5 space-y-4 opacity-50"}>
        {/* API key */}
        <label className="block">
          <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted">
            Anthropic API key
          </span>
          <div className="flex gap-2">
            <div className="relative flex-1">
              <input
                type={showKey ? "text" : "password"}
                value={apiKey}
                onChange={(e) => {
                  setApiKey(e.target.value);
                  setTest({ kind: "idle" });
                }}
                placeholder="sk-ant-…"
                autoComplete="off"
                spellCheck={false}
                className="w-full rounded-lg border border-border bg-bg px-3 py-2 pr-10 font-mono text-sm text-text placeholder:text-muted focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              />
              <button
                type="button"
                onClick={() => setShowKey((v) => !v)}
                aria-label={showKey ? "Hide key" : "Show key"}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted hover:text-text"
              >
                {showKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            <button
              onClick={runTest}
              disabled={test.kind === "testing"}
              className="inline-flex shrink-0 items-center gap-2 rounded-lg border border-border bg-surface-2 px-3 py-2 text-sm font-medium text-text transition-colors hover:bg-border/40 disabled:opacity-50"
            >
              {test.kind === "testing" ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              Test
            </button>
          </div>
          <a
            href="https://console.anthropic.com/settings/keys"
            target="_blank"
            rel="noreferrer"
            className="mt-1 inline-block text-xs text-primary hover:underline"
          >
            Get an API key →
          </a>
        </label>

        {test.kind === "ok" && (
          <p className="flex items-center gap-1.5 text-sm text-emerald-400">
            <CheckCircle2 className="h-4 w-4" /> {test.message}
          </p>
        )}
        {test.kind === "error" && (
          <p className="flex items-center gap-1.5 text-sm text-amber-400">
            <AlertTriangle className="h-4 w-4" /> {test.message}
          </p>
        )}

        {/* Model */}
        <label className="block">
          <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted">Model</span>
          <select
            value={model}
            onChange={(e) => setModel(e.target.value)}
            className="w-full rounded-lg border border-border bg-bg px-3 py-2 text-sm text-text focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
          >
            {AI_MODELS.map((m) => (
              <option key={m.id} value={m.id}>
                {m.label} — {m.hint}
              </option>
            ))}
          </select>
        </label>

        <div className="flex items-start gap-2 rounded-lg border border-border bg-surface-2/60 px-3 py-2.5 text-xs text-muted">
          <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-amber-400" />
          <span>
            Your key is stored locally in this browser only and sent directly to Anthropic. Use a
            key with usage limits, and avoid this on a shared device.
          </span>
        </div>
      </div>
    </section>
  );
}
